import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button Sizing Standard (cannafax.ai)
 * ─────────────────────────────────────
 * size="sm" (h-9 / 36px) — Toolbars, filter bars, card headers, inline row actions
 * size="default" (h-10 / 40px) — Dialog footers, form submit, standalone CTAs
 * size="lg" (h-11 / 44px) — Landing page hero CTAs, full-width auth buttons
 * size="icon" (h-10 w-10) — Icon-only buttons; use with h-9 w-9 className in toolbars
 *
 * Variant usage:
 * - "default" — Primary action (black fill)
 * - "destructive" — Delete/remove actions
 * - "outline" — Secondary actions (ring border, no CSS border)
 * - "ghost" — Tertiary/subtle actions
 * - "link" — Inline text links
 * - "google" — OAuth provider buttons
 * - "minimal" — Background-less subtle actions
 * - "quiet" — Secondary escape actions in dialogs/cards (Cancel, Close, Done): borderless text, full hit target
 * - "chip" — suggestion / filter pills (prompt starters, pinned questions); pair with size="chip"
 * - "guide" — Desk Setup / Your Next Move™ guided actions (crisp paper pill)
 * - "surface" — Framed selectable cards; natural content size, never a primary CTA
 *
 * NEVER add manual `border` or `bg-*` classes to override variants.
 * Use the variant system. If a new pattern is needed, add a variant here.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        guide: "rounded-full bg-background text-foreground ring-1 ring-desk-paper-edge shadow-sm font-semibold hover:bg-card hover:shadow",
        invitation: "invitation-fill text-invitation-foreground font-semibold tracking-tight focus-visible:ring-invitation",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "ring-1 ring-inset ring-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        google: "ring-1 ring-foreground bg-background text-foreground hover:bg-foreground hover:text-background transition-colors",
        minimal: "bg-transparent text-foreground hover:bg-secondary",
        chip: "rounded-full ring-1 ring-inset ring-border/70 bg-card text-muted-foreground font-normal hover:bg-muted hover:text-foreground aria-pressed:bg-foreground aria-pressed:text-background aria-pressed:ring-foreground/20",
        // Secondary text action: borderless, full hit target, soft hover wash, micro-press.
        quiet: "bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60 active:scale-[0.98] transition-[color,background-color,transform]",
        surface: "border border-border bg-card text-foreground hover:bg-muted/50 whitespace-normal font-normal aria-pressed:border-primary aria-pressed:bg-primary/5",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        chip: "h-8 px-3 text-xs [&_svg]:size-3",
        content: "h-auto p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
